import { KnownContentfulEnvironment, GameName } from "./types";

// Values provided here are needed in migrations, but are unfortunately not typed at the moment in there.
// https://www.contentful.com/developers/docs/extensibility/app-framework/editor-interfaces/

export const controlBuiltinWidgetIds = {
  assetLinkEditorDoNotUseThisReplacedByCustomApp: "assetLinkEditor",
  assetLinksEditor: "assetLinksEditor",
  assetGalleryEditor: "assetGalleryEditor",
  boolean: "boolean",
  datePicker: "datePicker",
  entryLinkEditor: "entryLinkEditor",
  entryLinksEditor: "entryLinksEditor",
  entryCardEditor: "entryCardEditor",
  entryCardsEditor: "entryCardsEditor",
  numberEditor: "numberEditor",
  rating: "rating",
  locationEditor: "locationEditor",
  objectEditor: "objectEditor",
  urlEditor: "urlEditor",
  slugEditor: "slugEditor",
  listInput: "listInput",
  checkbox: "checkbox",
  tagEditor: "tagEditor",
  multipleLine: "multipleLine",
  markdown: "markdown",
  singleLine: "singleLine",
  dropdown: "dropdown",
  radio: "radio",
  richTextEditor: "richTextEditor",
} as const;

export type ControlBuiltinWidgetId =
  (typeof controlBuiltinWidgetIds)[keyof typeof controlBuiltinWidgetIds];
export type UnallowedControlFieldsForTranslation =
  | "dropdown"
  | "radio"
  | "datePicker"
  | "urlEditor"
  | "boolean";

export const sidebarBuiltinWidgetIds = {
  "Status (publish button)": "publication-widget",
  "Entry tasks": "content-workflows-tasks-widget",
  "Content preview": "content-preview-widget",
  "Incoming links": "incoming-links-widget",
  "Translations (locale selection)": "translation-widget",
  "Entry-level versioning": "versions-widget",
  "Online editing users": "users-widget",
} as const;

export type SidebarBuiltinWidgetId =
  (typeof sidebarBuiltinWidgetIds)[keyof typeof sidebarBuiltinWidgetIds];

export type CustomApp =
  | "imagePicker"
  | "gameNotifications"
  | "contentfulEnvironment"
  | "selectWithLabel"
  | "pollStatus"
  | "compose" // Not a custom app, but listing it here lets us install it programmatically
  | "colorPicker"
  | "colorPickerForAllLocales"
  | "inboxHome"
  | "slug"
  | "languages"
  | "numberWithCopyField"
  | "textWithCopyField"
  | "linkToArticleField"
  | "analytics"
  | "livePreviewButton"
  | "buttonTarget"
  | "dropdownSetForAllLocales"
  | "milestones"
  | "relatedMedia"
  | "pollTargets"
  | "leaderboard"
  | "pollOptions"
  | "booleanForAllLocales"
  | "datePickerForAllLocales";

export type CustomApps = Record<CustomApp, string>;

const stagingAppIds = {
  imagePicker: "6ua9Heubn4IUgVxHlqoUQM",
  gameNotifications: "5HoRYZClYlLUgOAlkUANI2",
  contentfulEnvironment: "7CuXJyWxHl3em2q94JI5iQ",
  slug: "7CL9nYE9p0yE7Y6EAQIF76",
  selectWithLabel: "1T18ealzokT7fW8kmyUZkj",
  pollStatus: "6aHYYOaiCgntWMv6KwTtkt",
  compose: "6TfQEqkcINtj1MS0TuQTWJ",
  colorPicker: "4Vy3oAINwRgnxakoTz06tG",
  colorPickerForAllLocales: "6GmCOmGxCf4sNNfVZnLWez",
  inboxHome: "1YGsex4xZMwTgZ9fYDbEi8",
  analytics: "5Y2Acwm2aE3Y0D0yi2sLPD",
  languages: "1ekiyUXrTA0wsYyv9f9UZ0",
  numberWithCopyField: "3WBwv7djLcuYt5pJ1UKQBo",
  textWithCopyField: "1b7GI9ui7IRrkLTHQLxsSr",
  linkToArticleField: "2A0JGW52Gzyl8o8QgjuIO1",
  buttonTarget: "7ui6i2dQa34Qrs3IICBzV2",
  livePreviewButton: "16aSSuPUd8Kvm9bDg7Wn3m",
  dropdownSetForAllLocales: "6671T5CbwzhtTcldIqRqEA",
  milestones: "4EZZNHFnMc86azdhBsekBn",
  relatedMedia: "4td7X2smqXmbtVsoasF9HH",
  pollTargets: "3Rtd3o0r9Iy1edBAaT7yiT",
  leaderboard: "4Wy2N7ESBunpJQBUwFXoNK",
  pollOptions: "6JFdGOOPE6tG4abNrcFm0e",
  booleanForAllLocales: "2buWOfrFnWxTAizsm0UPdX",
  datePickerForAllLocales: "7oX3eRC4Sx1Zkd1nraOQGq",
};

export const EnvironmentSpecificAppIds = {
  staging: stagingAppIds,

  dev: stagingAppIds,

  // master === production in our case
  master: {
    imagePicker: "2Y3FXBIEv1EqqFCk7QOUUT",
    gameNotifications: "CVTxK12IZdRLVCsfCTSkd",
    contentfulEnvironment: "5cKKhBCessu3U5tX9nCUvq",
    slug: "2GsN8pNlvrcdaeCzyg5kMP",
    selectWithLabel: "45mptPyfrM1cr4tHWVnZRr",
    pollStatus: "3t9VFYzz45xPB5npXb1VzI",
    compose: "6TfQEqkcINtj1MS0TuQTWJ",
    colorPicker: "4Vy3oAINwRgnxakoTz06tG",
    colorPickerForAllLocales: "4NqFAL5qtMF7R5FmbJctlx",
    inboxHome: "7J2Zrq2wMRA7rPVEkpkNa8",
    analytics: "4TZYI1r5Oh3oNgz1dUxtBR",
    languages: "39OHphRhgvUWp659w2bSSE",
    numberWithCopyField: "5jzk3kwGBj3ei2TQYyWdd2",
    textWithCopyField: "6pPhPDEAJ6IXtytzeR517v",
    linkToArticleField: "4byXlAp712aoGp67TmMMB7",
    buttonTarget: "30qyLdUfDO7tI2NtAftOQN",
    livePreviewButton: "5sWxUn2inLXjCqBPYh6W5k",
    dropdownSetForAllLocales: "2SPE4fptgtMhv8avg928WR",
    milestones: "5hhlQx1oTLDi8tPw1DEYrM",
    relatedMedia: "156ee0GjwSoARZZVKhNumQ",
    pollTargets: "4DFrlaIHB7qq4YPKjFYBqW",
    leaderboard: "1Gxw6cER1KVKVkwvgHmtRV",
    pollOptions: "5krcvlIwJLFWXKYQtkM8Y",
    booleanForAllLocales: "3vvhynOkt0W1YAFpbPqK4z",
    datePickerForAllLocales: "3h7UGKtBDuyiXBIsAMLRgc",
  },
} as const;

// The Copy-to-other-environment sidebar (contentfulEnvironment) makes CMA calls
// into the *other* environment, and Contentful only permits those where the
// calling app definition is itself installed. Since the definition differs per
// environment, each environment also needs the other one's definition installed.
// Nothing references these in an editor interface — they exist purely to widen
// CMA access — so they must be listed here to be installed and to survive
// `apps:prune`, which would otherwise read them as wrong-environment drift.
// dev needs no extra: it runs the staging definition and only copies to staging.
export const CrossEnvironmentAppIds: {
  [key in KnownContentfulEnvironment]: string[];
} = {
  staging: [EnvironmentSpecificAppIds.master.contentfulEnvironment],
  dev: [],
  master: [stagingAppIds.contentfulEnvironment],
};

const AppToInstallationParams: { [key in CustomApp]?: unknown } = {
  colorPicker: {
    themes: [
      {
        id: "default",
        name: "Default",
        colors: [],
      },
    ],
  },
};

export const getParameters = (appName: CustomApp) => {
  return AppToInstallationParams[appName] ?? {};
};

export type UnallowedCustomAppsForTranslation =
  | "colorPicker"
  | "colorPickerForAllLocales"
  | "imagePicker"
  | "selectWithLabel"
  | "pollStatus"
  | "buttonTarget"
  | "dropdownSetForAllLocales"
  | "relatedMedia"
  | "pollTargets"
  | "booleanForAllLocales"
  | "datePickerForAllLocales";

export type AllowedCustomAppsForTranslation = Exclude<
  CustomApp,
  UnallowedCustomAppsForTranslation
>;

type AppId = Exclude<
  (typeof EnvironmentSpecificAppIds)[keyof typeof EnvironmentSpecificAppIds][CustomApp],
  null
>;

export const auditWebhookUrlBase =
  "https://vscm5zq2ttnxtagwn2acz5ehyi0swufg.lambda-url.us-east-1.on.aws/";

export const getCustomAppId = (id: CustomApp, env?: string): AppId => {
  const possibleEnv = KnownContentfulEnvironment.safeParse(
    env ?? process.env.CONTENTFUL_ENVIRONMENT,
  );

  const knownEnv: KnownContentfulEnvironment = possibleEnv.success
    ? possibleEnv.data
    : "staging";

  const appId = EnvironmentSpecificAppIds[knownEnv][id];

  if (!appId) {
    throw new Error(`No app id found for ${id} in ${knownEnv}`);
  }

  return appId;
};

export const getPollBaseUrl = (): string => {
  const possibleEnv = KnownContentfulEnvironment.safeParse(
    process.env.CONTENTFUL_ENVIRONMENT,
  );

  const gameNameRes = GameName.safeParse(process.env.GAMENAME);

  if (!gameNameRes.success) {
    return "";
  }

  const gameName = gameNameRes.data;

  if (!possibleEnv.success) {
    return `https://${gameName}-api.inbox-dev.supercell.com/poll-api/poll/admin/`;
  }

  switch (possibleEnv.data) {
    case "master":
      return `https://${gameName}-api.inbox.supercell.com/poll-api/poll/admin/`;
    case "staging":
      return `https://${gameName}-api.inbox-staging.supercell.com/poll-api/poll/admin/`;
    case "dev":
      return `https://${gameName}-api.inbox-dev.supercell.com/poll-api/poll/admin/`;
  }
};

export const getTranslationBaseUrl = (): string => {
  const possibleEnv = KnownContentfulEnvironment.safeParse(
    process.env.CONTENTFUL_ENVIRONMENT,
  );

  const gameNameRes = GameName.safeParse(process.env.GAMENAME);

  if (!gameNameRes.success) {
    return "";
  }

  const gameName = gameNameRes.data;

  if (!possibleEnv.success) {
    return `https://${gameName}-api.inbox-dev.supercell.com/phrase/`;
  }

  switch (possibleEnv.data) {
    case "master":
      return `https://${gameName}-api.inbox.supercell.com/phrase/`;
    case "staging":
      return `https://${gameName}-api.inbox-staging.supercell.com/phrase/`;
    case "dev":
      return `https://${gameName}-api.inbox-dev.supercell.com/phrase/`;
  }
};

export const getAnalyticsBaseUrl = (): string => {
  const possibleEnv = KnownContentfulEnvironment.safeParse(
    process.env.CONTENTFUL_ENVIRONMENT,
  );

  const gameNameRes = GameName.safeParse(process.env.GAMENAME);

  if (!gameNameRes.success) {
    return "";
  }

  const gameName = gameNameRes.data;

  if (!possibleEnv.success) {
    return `https://${gameName}-api.inbox-dev.supercell.com/databricks-connector/query`;
  }

  switch (possibleEnv.data) {
    case "master":
      return `https://${gameName}-api.inbox.supercell.com/databricks-connector/query`;
    case "staging":
      return `https://${gameName}-api.inbox-staging.supercell.com/databricks-connector/query`;
    case "dev":
      return `https://${gameName}-api.inbox-dev.supercell.com/databricks-connector/query`;
  }
};

export const MAXIMUM_IMAGE_SIZE = 20_971_520; // 20 MB is the maximum size supported by Contentful images API.

export const spaceIdToGame: { [key: string]: GameName } = {
  xdjcscmv3zo3: "brawlstars",
  lqe2co20rkhw: "clashofclans",
  "0rebc2l8z0wu": "hayday",
  epg71hpoul0m: "squadbusters",
  "9jtsgmsiuthj": "clashroyale",
  cf7umh1jvp53: "boombeach",
  kpmhkcp4p1s7: "rogue",
};
export const gameToSpaceId: { [key in GameName]?: string } = Object.fromEntries(
  Object.entries(spaceIdToGame).map(([k, v]) => [GameName.parse(v), k]),
);
